import { FunctionComponent, useEffect, useState } from 'react'
import { Button, InputCheckbox, SearchInput, InlineAlert } from '../../../../components/BaseComponents'
import CanonicalModal from '../../../../components/BaseComponents/CanonicalModal/CanonicalModal'
import { SortConfig } from '../../../../components/BaseComponents/Datagrid/useSortableData'
import Pagination from '../../../../components/BaseComponents/Pagination/Pagination'
import SimpleSortTable, {
	TableColumn,
} from '../../../../components/BaseComponents/SimpleSortTable/SimpleSortTable'
import { useEffectWithoutInitialRender } from '../../../../hooks/useEffectWithoutInitialRender'
import { useDebouncedSearch } from '../../../../hooks/useDebouncedSearch'
import { GET_SUPPLIERS_LIMIT } from '../../../../redux/actions/suppliersActions'
import { useAppDispatch } from '../../../../redux/hooks/reduxHooks'
import { SUPPLIERS_RESET } from '../../../../redux/slices/suppliersSlice'
import { FetchStatus } from '../../../../types/LoadingStates'
import { Supplier } from '../../../../types/Supplier'
import { WORST_SUPPLIER_LIMIT_QUANTITY } from '../Graphs/ECO2Supplier'
import useApi from '../../../../hooks/useApi'
import { useSupplierOptions } from './useSelectionLookups'

// This component should only refresh/be doing stuff when 'open' as it lives inside a modal and
// might otherwise be rendered invisibly and cause interference

type IModalSupplierSearch = {
	open: boolean
	onClose: () => void
	onChange?: (newValue?: Supplier[]) => void
	sectorId?: string | number
}

export const ModalSupplierSearch: FunctionComponent<IModalSupplierSearch> = ({
	open,
	onClose,
	onChange,
	sectorId
}) => {
	const dispatch = useAppDispatch()
	const api = useApi();

	const suppliers = useSupplierOptions(api, open)
	const { current, status: suppliersStatus, error: suppliersError, pagination, suppliers: allSuppliers } = suppliers

	const [searchKeyword, setSearchKeyword] = useState('')

	const [orderBy, setOrderBy] = useState<string>('name')
	const [direction, setDirection] = useState<'asc' | 'desc'>('asc')

	const [selection, setSelection] = useState<Supplier[]>([])

	// Data loading (refire every time the modal opens)
	useEffect(() => {
		if (open) {
			setSelection([])
			setSearchKeyword('')
			setOrderBy('name')
			setDirection('asc')
			dispatch(SUPPLIERS_RESET())
			suppliers.load({ sectorCodes: sectorId })
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, api, sectorId])
	useEffectWithoutInitialRender(() => {
		if (current && suppliersStatus === FetchStatus.loaded && sectorId !== undefined && sectorId !== null) {
			onChange && onChange(allSuppliers)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [suppliersStatus, current])

	// DataGrid Columns
	const columns: TableColumn[] = [
		{
			id: 'selected',
			label: '',
			sortable: false,
			columnClass: 'w-3', // narrow fixed column width
			renderer: (columnKey, rowData) => {
				return (
					<InputCheckbox
						className='rounded border-charcoal-300 text-algae-600'
						value={rowData._id}
						checked={rowData.selected}
						onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
							event.stopPropagation()

							if (selection.filter((supplier) => supplier.id === rowData._id).length > 0) {
								// Remove from selection
								setSelection([...selection.filter((supplier) => supplier.id !== rowData._id)])
							} else {
								// Check there's still room to add another
								if (selection.length !== WORST_SUPPLIER_LIMIT_QUANTITY) {
									// Add to selected
									setSelection([...selection, rowData.supplierObject])
								}
							}
						}}
					/>
				)
			},
		},
		{ id: 'name', label: 'Supplier', sortable: true },
		{
			id: 'isicCode',
			label: 'Sector Code (ISIC)',
			sortable: false,
			columnClass: 'col-span-2',
			renderer: (columnKey, rowData) => {
				return (
					<>
						<p className='text-white'>
							{rowData.isicCode}
							{rowData.isicCodeName && (
								<span className='text-charcoal-050'> - {rowData.isicCodeName}</span>
							)}
						</p>
					</>
				)
			},
		},
		{
			id: 'jurisdiction',
			label: 'Jurisdiction',
			sortable: false,
			renderer: (columnKey, rowData) => {
				return (
					<>
						<p className='text-white'>{rowData.jurisdiction_code}</p>
					</>
				)
			},
		},
	]
	const defaultSorting: SortConfig = {
		key: 'name',
		direction: 'ascending',
	}

	// DataGrid data constructor
	const constructData = (dataSet: Array<Supplier>) => [
		...dataSet.map((supplier) => {
			return {
				_id: supplier.id,
				name: supplier.name,
				isicCode: supplier.supplierIndustryCode?.code || '-',
				isicCodeName: supplier.supplierIndustryCode?.name || undefined,
				jurisdiction_code: supplier.jurisdiction_code || '-',
				selected:
					selection.filter((selectedSupplier) => selectedSupplier.id === supplier.id).length > 0,
				supplierObject: supplier,
			}
		}),
	]

	// Timeout for search
	const runSearch = () => {
		suppliers.load({
			...suppliers.requested, page: pagination.first_page, limit: GET_SUPPLIERS_LIMIT,
			orderBy, direction: 'asc', q: searchKeyword,
		})
	}
	const { searchInProgress, flush: flushSearch } = useDebouncedSearch(searchKeyword, runSearch)

	return (
		<CanonicalModal
			open={open}
			onClose={onClose}
			size='small'
			title='Select suppliers to compare'
			actions={
				selection.length > 0 ? (
					<Button
						type='Primary'
						text='Save selection'
						disabled={!current || suppliersStatus !== FetchStatus.loaded}
						onClick={() => {
							onChange && onChange(selection)
						}}
					/>
				) : null
			}
		>
			{current && suppliersStatus === FetchStatus.error && (
				<InlineAlert type='error' className='mt-4'>					<p className='text-white'>
						There was an error fetching suppliers associated with this organisation. If this problem
						persists, please contact the Neoni team.
					</p>
					<p className='text-white'>{suppliersError}</p>
					<Button type='Primary' text='Retry' onClick={suppliers.retry} />
				</InlineAlert>
			)}

			<p className='text-white mt-8 mb-4'>
				Please select up to {WORST_SUPPLIER_LIMIT_QUANTITY} suppliers to compare their embedded CO₂
				emissions:
			</p>

			{current && selection.length > 0 && (
				<>
					{selection.map((supplier) => {
						return (
							<Button
								key={supplier.id}
								type='Outlined'
								size='sm'
								additionalClass='mr-2 mb-2'
								text={supplier.name}
								hasTrailingIcon
								icon='fa-solid fa-xmark'
								onClick={() => {
									// Remove from selection
									setSelection([
										...selection.filter((selectedSupplier) => selectedSupplier.id !== supplier.id),
									])
								}}
							/>
						)
					})}
				</>
			)}

			<div className='items-start mt-4'>
				<SearchInput
					name='filterSuppliers'
					placeholder='Search for a supplier'
					value={searchKeyword}
					onChange={(event) => {
						setSearchKeyword(event.target.value)
					}}
					onKeyDown={(event) => {
						if (event.key === 'Enter') {
							flushSearch()
						}
					}}
					onClear={() => {
						setSearchKeyword('')
						suppliers.load({
							...suppliers.requested, page: pagination.first_page, limit: GET_SUPPLIERS_LIMIT,
							orderBy, direction, q: '',
						})
					}}
					isLoading={searchInProgress}
					className='max-w-2xl'
				/>
			</div>

			{current && suppliersStatus !== FetchStatus.error && <><SimpleSortTable
				isLoading={suppliersStatus === FetchStatus.loading}
				additionalClass='mt-4'
				data={constructData(allSuppliers)}
				columns={columns}
				defaultSorting={defaultSorting}
				onSortPress={(key, direction) => {
					// Store current sorting values for pagination to reference in its API calls
					setOrderBy(key)
					setDirection(direction === 'ascending' ? 'asc' : 'desc')
					suppliers.load({
						...suppliers.requested, page: pagination.current_page, limit: GET_SUPPLIERS_LIMIT,
						orderBy: key, direction: direction === 'ascending' ? 'asc' : 'desc', q: searchKeyword,
					})
				}}
			/>

			<Pagination
				isLoading={suppliersStatus === FetchStatus.loading}
				pagination={pagination}
				onPageChange={(newPageNumber) => {
					suppliers.load({
						...suppliers.requested, page: newPageNumber, limit: GET_SUPPLIERS_LIMIT,
						orderBy, direction, q: searchKeyword,
					})
				}}
			/></>}
		</CanonicalModal>
	)
}
